import type { RootState } from 'store';
import type { BinderFormStateKey, FormStateKey } from './stepTypes';

import { PROVINCE_NOVA_SCOTIA } from 'variables/constants';

export const getBrokerEmail = (
  state: RootState,
  formStateKey: BinderFormStateKey,
) => {
  if (formStateKey === 'autoPersonalForm') {
    return state.autoPersonalForm.data.company_and_broker_information
      .broker_email;
  }
  if (formStateKey === 'commercialForm') {
    return state.commercialForm.data.broker_information.email;
  }
  return state.homePersonalForm.data.broker_information.email;
};

export const getApplicantEmail = (
  state: RootState,
  formStateKey: BinderFormStateKey,
) => {
  if (formStateKey === 'autoPersonalForm') {
    return state.autoPersonalForm.data.applicant.email;
  }
  if (formStateKey === 'commercialForm') {
    return state.commercialForm.data.applicant_email;
  }
  return state.homePersonalForm.data.applicants[0].email;
};

export const getCoApplicantEmail = (
  state: RootState,
  formStateKey: BinderFormStateKey,
) => {
  if (formStateKey === 'autoPersonalForm') {
    return state.autoPersonalForm.data.co_applicant.email;
  }
  if (formStateKey === 'commercialForm') {
    return '';
  }
  return state.homePersonalForm.data.applicants[1].email;
};

export const getAccountHolders = (
  state: RootState,
  formStateKey: FormStateKey,
) => {
  return state[formStateKey].data.account_holders;
};

export const getShowAccountHolderEmails = (
  state: RootState,
  formStateKey: FormStateKey,
) => {
  const paymentSchedule = state[formStateKey].data.billing.payment_schedule;
  if (formStateKey === 'autoPersonalForm') {
    return paymentSchedule !== 'annual';
  }
  return !!paymentSchedule && paymentSchedule !== 'annual';
};

export const getSigningEmails = (
  state: RootState,
  formStateKey: BinderFormStateKey,
) => {
  if (formStateKey === 'commercialForm') {
    const commercialCoApplicants =
      state.commercialForm.data.co_applicants || [];
    return {
      brokerEmail: getBrokerEmail(state, formStateKey),
      applicantEmail: getApplicantEmail(state, formStateKey),
      coApplicantEmail: '',
      accountHolderSameAsApplicant: false,
      accountHolderSameAsCoApplicant: false,
      accountHolderEmail: '',
      secondaryAccountHolder: '',
      secondAccountHolderSameAsCoApplicant: false,
      showAccountHolderEmails: false,
      retireeEmails: [] as string[],
      excludedDriverEmails: [] as string[],
      additionalInsureds: [] as Array<{ email?: string }>,
      commercialCoApplicants,
      supervisor: null as { email: string; name?: string } | null,
    };
  }

  const report =
    formStateKey === 'autoPersonalForm'
      ? state.autoPersonalForm.data.report_of_broker_agent
      : undefined;
  const supervisor =
    formStateKey === 'autoPersonalForm' &&
    state.autoPersonalForm.province === PROVINCE_NOVA_SCOTIA &&
    report?.broker_level === 'Level 1' &&
    report?.supervisor_email
      ? {
          email: report.supervisor_email,
          name: report.supervisor_name || undefined,
        }
      : null;

  const accountHolders = getAccountHolders(state, formStateKey);
  const retireeEmails =
    formStateKey === 'autoPersonalForm'
      ? (state.autoPersonalForm.data.drivers || [])
          .filter(
            (driver) => driver.is_retiree_discount && driver.retiree_email,
          )
          .map((driver) => driver.retiree_email.trim())
          .filter(
            (email, index, emails) => email && emails.indexOf(email) === index,
          )
      : [];
  const excludedDriverEmails =
    formStateKey === 'autoPersonalForm'
      ? (state.autoPersonalForm.data.drivers || [])
          .filter(
            (driver) =>
              driver.is_excluded_driver && driver.excluded_driver_email,
          )
          .map((driver) => driver.excluded_driver_email.trim())
          .filter(
            (email, index, emails) => email && emails.indexOf(email) === index,
          )
      : [];
  const additionalInsureds = state[formStateKey].data.additional_insureds ?? [];

  return {
    brokerEmail: getBrokerEmail(state, formStateKey),
    applicantEmail: getApplicantEmail(state, formStateKey),
    coApplicantEmail: getCoApplicantEmail(state, formStateKey),
    accountHolderSameAsApplicant: accountHolders.first.is_same_as_applicant,
    accountHolderSameAsCoApplicant:
      accountHolders.first.is_same_as_co_applicant,
    accountHolderEmail: accountHolders.first.email,
    secondaryAccountHolder: accountHolders.second.email,
    secondAccountHolderSameAsCoApplicant:
      accountHolders.second.is_same_as_co_applicant,
    showAccountHolderEmails: getShowAccountHolderEmails(state, formStateKey),
    retireeEmails,
    excludedDriverEmails,
    additionalInsureds,
    commercialCoApplicants: [] as Array<{ name?: string; email?: string }>,
    supervisor,
  };
};
